import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { 
  Cloud, Sun, CloudRain, Wind, Users, Bell, Send, 
  RefreshCw, Calendar, Thermometer, Gift, ShoppingCart,
  Droplets, Eye, AlertTriangle, Check, Plane, LogOut,
  MessageSquare, BarChart3, Cake, Clock, Play, Smartphone,
  Plus, Trash2, CheckCircle, XCircle, FileText, Key, Megaphone, Info, Zap,
  Copy, ChevronDown, ChevronUp, Activity, Shield, Apple, Monitor, 
  History, Server, Wifi, WifiOff, Search, Filter, TestTube
} from 'lucide-react';
import { toast } from 'sonner';
import AdminLayout from './admin/AdminLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SmartNotificationsPage = () => {
  const [weather, setWeather] = useState(null);
  const [weatherAnalysis, setWeatherAnalysis] = useState(null);
  const [forecast, setForecast] = useState([]);
  const [guestsInStay, setGuestsInStay] = useState([]);
  const [guestsArriving, setGuestsArriving] = useState([]);
  const [guestsCheckedOut, setGuestsCheckedOut] = useState([]);
  const [abandonedQuotes, setAbandonedQuotes] = useState([]);
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [discountCodes, setDiscountCodes] = useState([]);
  const [devices, setDevices] = useState([]);
  const [pushLogs, setPushLogs] = useState([]);
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [lastRunResult, setLastRunResult] = useState(null);
  const [activeTab, setActiveTab] = useState('devices');
  
  // Device Management Center states
  const [deviceStats, setDeviceStats] = useState(null);
  const [pushHealth, setPushHealth] = useState(null);
  const [deviceSearchQuery, setDeviceSearchQuery] = useState('');
  const [devicePlatformFilter, setDevicePlatformFilter] = useState('all');
  const [deviceStatusFilter, setDeviceStatusFilter] = useState('all');
  const [deviceAdminFilter, setDeviceAdminFilter] = useState('all');
  const [expandedDevice, setExpandedDevice] = useState(null);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [showTokenDialog, setShowTokenDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const [deviceHistory, setDeviceHistory] = useState([]);
  
  // New dialogs for verification and preview
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [showVerifyDialog, setShowVerifyDialog] = useState(false);
  const [verifyData, setVerifyData] = useState(null);
  const [showTestResultDialog, setShowTestResultDialog] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [showBulkConfirmDialog, setShowBulkConfirmDialog] = useState(false);
  const [bulkPreview, setBulkPreview] = useState(null);
  
  // Custom notification dialog
  const [customDialogOpen, setCustomDialogOpen] = useState(false);
  const [customNotification, setCustomNotification] = useState({
    title: '',
    body: '',
    targetType: 'all', // 'all', 'guests_in_stay', 'arriving_soon', 'selected'
    selectedDevices: [],
    selectedGuests: [],
    notificationType: 'promo', // 'promo', 'info', 'alert', 'reminder'
    actionUrl: '',
    sendEntryCode: false
  });

  const token = localStorage.getItem('token');

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const headers = { Authorization: `Bearer ${token}` };

      // Fetch all data in parallel
      const [
        weatherRes, forecastRes, guestsRes, arrivingRes, 
        checkedOutRes, quotesRes, logsRes, statsRes, 
        codesRes, devicesFullRes, pushLogsRes, schedulerRes,
        deviceStatsRes, healthRes
      ] = await Promise.all([
        fetch(`${API_URL}/api/admin/smart-notifications/weather`, { headers }),
        fetch(`${API_URL}/api/admin/smart-notifications/forecast?days=5`, { headers }),
        fetch(`${API_URL}/api/admin/smart-notifications/guests-in-stay`, { headers }),
        fetch(`${API_URL}/api/admin/smart-notifications/arriving-soon?days=3`, { headers }),
        fetch(`${API_URL}/api/admin/smart-notifications/checked-out?days=7`, { headers }),
        fetch(`${API_URL}/api/admin/smart-notifications/abandoned-quotes?hours=48`, { headers }),
        fetch(`${API_URL}/api/admin/smart-notifications/logs?limit=30`, { headers }),
        fetch(`${API_URL}/api/admin/smart-notifications/stats`, { headers }),
        fetch(`${API_URL}/api/admin/discount-codes`, { headers }),
        fetch(`${API_URL}/api/admin/push/devices/full`, { headers }),
        fetch(`${API_URL}/api/admin/push/logs?limit=20`, { headers }),
        fetch(`${API_URL}/api/admin/scheduler/status`, { headers }),
        fetch(`${API_URL}/api/admin/push/devices/stats`, { headers }),
        fetch(`${API_URL}/api/admin/push/health`, { headers })
      ]);

      if (weatherRes.ok) {
        const data = await weatherRes.json();
        setWeather(data.weather);
        setWeatherAnalysis(data.analysis);
      }
      if (forecastRes.ok) setForecast((await forecastRes.json()).forecast || []);
      if (guestsRes.ok) setGuestsInStay((await guestsRes.json()).guests || []);
      if (arrivingRes.ok) setGuestsArriving((await arrivingRes.json()).guests || []);
      if (checkedOutRes.ok) setGuestsCheckedOut((await checkedOutRes.json()).guests || []);
      if (quotesRes.ok) setAbandonedQuotes((await quotesRes.json()).quotes || []);
      if (logsRes.ok) setLogs((await logsRes.json()).logs || []);
      if (statsRes.ok) setStats(await statsRes.json());
      if (codesRes.ok) setDiscountCodes((await codesRes.json()).codes || []);
      if (devicesFullRes.ok) {
        const deviceData = await devicesFullRes.json();
        setDevices(deviceData.devices || []);
      }
      if (pushLogsRes.ok) setPushLogs(await pushLogsRes.json());
      if (schedulerRes.ok) setSchedulerStatus(await schedulerRes.json());
      if (deviceStatsRes.ok) setDeviceStats(await deviceStatsRes.json());
      if (healthRes.ok) setPushHealth(await healthRes.json());

    } catch (err) {
      setError('Errore nel caricamento dei dati');
      console.error(err);
    }
    setLoading(false);
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ==================== DEVICE MANAGEMENT FUNCTIONS ====================
  
  const headers = { Authorization: `Bearer ${token}` };

  // Filter devices
  const filteredDevices = devices.filter(device => {
    if (deviceSearchQuery) {
      const query = deviceSearchQuery.toLowerCase();
      const matchesSearch = (
        (device.user_nome || '').toLowerCase().includes(query) ||
        (device.user_email || '').toLowerCase().includes(query) ||
        (device.device_name || '').toLowerCase().includes(query) ||
        (device.token || '').toLowerCase().includes(query)
      );
      if (!matchesSearch) return false;
    }
    if (devicePlatformFilter !== 'all') {
      if ((device.platform || '').toLowerCase() !== devicePlatformFilter) return false;
    }
    if (deviceStatusFilter === 'active' && !device.is_active) return false;
    if (deviceStatusFilter === 'inactive' && device.is_active) return false;
    if (deviceStatusFilter === 'invalid' && device.token_status !== 'invalid') return false;
    if (deviceAdminFilter === 'admin' && !device.is_admin_device) return false;
    if (deviceAdminFilter === 'guest' && device.is_admin_device) return false;
    return true;
  });

  const getPlatformIcon = (platform) => {
    const p = (platform || '').toLowerCase();
    if (p.includes('ios') || p.includes('iphone') || p.includes('ipad')) {
      return <Apple className="w-5 h-5 text-gray-700" />;
    }
    if (p.includes('android')) {
      return <Smartphone className="w-5 h-5 text-green-600" />;
    }
    return <Monitor className="w-5 h-5 text-blue-600" />;
  };

  const getStatusBadge = (device) => {
    if (device.token_status === 'invalid') {
      return <Badge variant="destructive" className="gap-1"><XCircle className="w-3 h-3" /> Non valido</Badge>;
    }
    if (!device.is_active) {
      return <Badge variant="secondary" className="gap-1 bg-gray-200"><WifiOff className="w-3 h-3" /> Offline</Badge>;
    }
    return <Badge variant="default" className="gap-1 bg-green-500"><CheckCircle className="w-3 h-3" /> Attivo</Badge>;
  };

  const copyToken = (deviceToken) => {
    navigator.clipboard.writeText(deviceToken);
    toast.success('Token copiato negli appunti');
  };

  const deleteDevice = async (deviceId) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/push/devices/${deviceId}`, {
        method: 'DELETE',
        headers
      });
      if (res.ok) {
        toast.success('Dispositivo rimosso');
        fetchData();
        setShowDeleteDialog(false);
        setSelectedDevice(null);
      } else {
        toast.error('Errore nella rimozione');
      }
    } catch (err) {
      toast.error('Errore di connessione');
    }
  };

  const bulkTest = async (target, skipConfirm = false) => {
    // If not skipping confirm, show preview first
    if (!skipConfirm) {
      setSending(true);
      try {
        const res = await fetch(`${API_URL}/api/admin/push/bulk-test?target=${target}&preview_only=true`, {
          method: 'POST',
          headers
        });
        const data = await res.json();
        setBulkPreview({ ...data, target });
        setShowBulkConfirmDialog(true);
      } catch (err) {
        toast.error('Errore nel caricamento anteprima');
      }
      setSending(false);
      return;
    }
    
    // Actually send
    setSending(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/push/bulk-test?target=${target}`, {
        method: 'POST',
        headers
      });
      const data = await res.json();
      setShowBulkConfirmDialog(false);
      if (data.success) {
        toast.success(`Inviate ${data.summary?.accepted_by_firebase || data.sent || 0} notifiche`);
      } else {
        toast.error(data.error || 'Errore nell\'invio');
      }
      // Show detailed result
      setTestResult(data);
      setShowTestResultDialog(true);
    } catch (err) {
      toast.error('Errore di connessione');
    }
    setSending(false);
  };

  const previewRecipients = async (target) => {
    setSending(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/push/preview-recipients?target=${target}`, { headers });
      if (res.ok) {
        const data = await res.json();
        setPreviewData(data);
        setShowPreviewDialog(true);
      }
    } catch (err) {
      toast.error('Errore nel caricamento anteprima');
    }
    setSending(false);
  };

  const verifyAdminDevices = async () => {
    setSending(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/push/verify-admin-devices`, { headers });
      if (res.ok) {
        const data = await res.json();
        setVerifyData(data);
        setShowVerifyDialog(true);
      }
    } catch (err) {
      toast.error('Errore nella verifica');
    }
    setSending(false);
  };

  const testSingleDevice = async (deviceId) => {
    setSending(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/push/devices/${deviceId}/test`, {
        method: 'POST',
        headers
      });
      const data = await res.json();
      setTestResult(data);
      setShowTestResultDialog(true);
      if (data.success) {
        toast.success(`Notifica accettata da Firebase`);
      } else {
        toast.error(data.status_message || 'Errore invio notifica');
      }
    } catch (err) {
      toast.error('Errore di connessione');
    }
    setSending(false);
  };

  const fetchDeviceHistory = async (deviceId) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/push/devices/${deviceId}/notifications`, { headers });
      if (res.ok) {
        const data = await res.json();
        setDeviceHistory(data.notifications || []);
        setShowHistoryDialog(true);
      }
    } catch (err) {
      toast.error('Errore nel caricamento storico');
    }
  };

  const cleanupInactive = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/push/cleanup-inactive`, {
        method: 'DELETE',
        headers
      });
      const data = await res.json();
      if (data.success) {
        toast.success(data.message);
        fetchData();
      }
    } catch (err) {
      toast.error('Errore nella pulizia');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // ==================== END DEVICE MANAGEMENT FUNCTIONS ====================

  const runNotifications = async (type) => {
    setSending(true);
    setLastRunResult(null);
    try {
      const endpoint = type === 'all' 
        ? '/api/admin/smart-notifications/run-all'
        : `/api/admin/smart-notifications/run-${type}`;
      
      const res = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (res.ok) {
        const data = await res.json();
        setLastRunResult(data);
        toast.success(`Notifiche inviate: ${data.total_sent || data.notifications_sent || 0}`);
        fetchData();
      }
    } catch (err) {
      console.error(err);
      toast.error('Errore nell\'invio delle notifiche');
      setLastRunResult({ errors: [err.message] });
    }
    setSending(false);
  };

  const sendCustomNotification = async () => {
    if (!customNotification.title || !customNotification.body) {
      toast.error('Inserisci titolo e messaggio');
      return;
    }

    setSending(true);
    try {
      const payload = {
        title: customNotification.title,
        body: customNotification.body,
        data: { 
          type: customNotification.notificationType,
          action_url: customNotification.actionUrl || undefined
        }
      };

      // Determina i destinatari in base al targetType
      if (customNotification.targetType === 'selected' && customNotification.selectedDevices.length > 0) {
        payload.device_tokens = customNotification.selectedDevices;
      } else if (customNotification.targetType === 'guests_in_stay') {
        // Ottieni token solo degli ospiti in soggiorno
        const guestUserIds = guestsInStay.map(g => g.user_id).filter(Boolean);
        const guestDevices = devices.filter(d => guestUserIds.includes(d.user_id));
        if (guestDevices.length > 0) {
          payload.device_tokens = guestDevices.map(d => d.token);
        } else {
          // Nessun dispositivo trovato per ospiti in soggiorno - non inviare a nessuno
          toast.warning('Nessun dispositivo registrato per gli ospiti in soggiorno');
          setSending(false);
          return;
        }
      } else if (customNotification.targetType === 'arriving_soon') {
        // Ottieni token solo degli ospiti in arrivo
        const arrivingUserIds = guestsArriving.map(g => g.user_id).filter(Boolean);
        const arrivingDevices = devices.filter(d => arrivingUserIds.includes(d.user_id));
        if (arrivingDevices.length > 0) {
          payload.device_tokens = arrivingDevices.map(d => d.token);
        } else {
          // Nessun dispositivo trovato per ospiti in arrivo - non inviare a nessuno
          toast.warning('Nessun dispositivo registrato per gli ospiti in arrivo');
          setSending(false);
          return;
        }
      }
      // else 'all' - non passa device_tokens, invia a tutti

      const res = await fetch(`${API_URL}/api/admin/push/send`, {
        method: 'POST',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        const data = await res.json();
        if (data.status === 'NO_DEVICES' || data.status === 'NO_DEVICE_TOKEN') {
          toast.warning(data.message || 'Nessun dispositivo registrato');
        } else {
          toast.success(`Inviate: ${data.sent}, Fallite: ${data.failed}`);
        }
        setCustomDialogOpen(false);
        setCustomNotification({ 
          title: '', body: '', targetType: 'all', selectedDevices: [], 
          selectedGuests: [], notificationType: 'promo', actionUrl: '', sendEntryCode: false 
        });
        fetchData();
      } else {
        const errorData = await res.json().catch(() => ({}));
        const errorDetail = errorData.detail || 'Errore nell\'invio';
        console.error('Push notification error:', errorData);
        
        // Show specific error messages
        if (errorDetail.includes('FIREBASE_NOT_CONFIGURED')) {
          toast.error('Firebase non configurato. Contatta l\'amministratore.');
        } else if (errorDetail.includes('FIREBASE_NOT_INITIALIZED')) {
          toast.error('Servizio push non disponibile al momento.');
        } else {
          toast.error(errorDetail);
        }
      }
    } catch (err) {
      console.error('Push notification exception:', err);
      toast.error('Errore di connessione nell\'invio della notifica');
    }
    setSending(false);
  };

  const toggleDeviceSelection = (deviceToken) => {
    setCustomNotification(prev => ({
      ...prev,
      selectedDevices: prev.selectedDevices.includes(deviceToken)
        ? prev.selectedDevices.filter(t => t !== deviceToken)
        : [...prev.selectedDevices, deviceToken]
    }));
  };

  const getWeatherIcon = (analysis) => {
    if (!analysis) return <Cloud className="w-8 h-8 text-gray-400" />;
    if (analysis.is_rainy) return <CloudRain className="w-8 h-8 text-blue-500" />;
    if (analysis.is_windy) return <Wind className="w-8 h-8 text-gray-500" />;
    if (analysis.is_perfect) return <Sun className="w-8 h-8 text-yellow-500" />;
    if (analysis.is_beach_day) return <Sun className="w-8 h-8 text-orange-400" />;
    return <Cloud className="w-8 h-8 text-gray-400" />;
  };

  const getNotificationTypeIcon = (type) => {
    const icons = {
      'beach': '🏖️', 'restaurant': '🍽️', 'experience': '✨', 'gelateria': '🍦',
      'caseificio': '🧀', 'event': '🎉', 'weather_alert': '⚠️', 'beach_perfect': '🌟',
      'pre_arrival_weather': '🌤️', 'pre_arrival_checkin': '📝', 'pre_arrival_info': '🚗',
      'pre_arrival_tomorrow': '🏠', 'post_stay_thanks': '💚', 'post_stay_review': '⭐',
      'post_stay_discount': '🎁', 'recovery': '🔔', 'birthday': '🎂', 'custom': '📱'
    };
    return icons[type] || '📱';
  };

  const getDevicePlatformIcon = (platform) => {
    if (platform === 'ios') return '🍎';
    if (platform === 'android') return '🤖';
    return '📱';
  };

  const sendEntryCode = async (bookingId) => {
    try {
      setSending(true);
      const res = await fetch(`${API_URL}/api/admin/push/send-entry-code/${bookingId}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const data = await res.json();
      
      if (data.success) {
        toast.success(`🔑 Codice ${data.codice_ingresso} inviato a ${data.devices_sent} dispositivo/i`);
      } else {
        toast.error(data.error || 'Errore nell\'invio del codice');
        if (data.codice_ingresso) {
          toast.info(`Il codice è: ${data.codice_ingresso}`);
        }
      }
    } catch (err) {
      toast.error('Errore nell\'invio del codice');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-[#1B4332]" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-[#1B4332]">Notifiche Push</h1>
            <p className="text-gray-500 mt-1">Sistema intelligente di notifiche push iOS/Android</p>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={fetchData} disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Aggiorna
            </Button>
            
            <Button variant="outline" onClick={() => window.location.href = '/admin/notification-templates'}>
              <FileText className="w-4 h-4 mr-2" />
              Template
            </Button>
            
            {/* Custom Notification Dialog */}
            <Dialog open={customDialogOpen} onOpenChange={setCustomDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="border-[#1B4332] text-[#1B4332]">
                  <Megaphone className="w-4 h-4 mr-2" />
                  Push Manuale
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Megaphone className="w-5 h-5 text-[#1B4332]" />
                    Invia Notifica Push Manuale
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-5 py-4">
                  {devices.length === 0 ? (
                    <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-center">
                      <AlertTriangle className="w-8 h-8 text-yellow-500 mx-auto mb-2" />
                      <p className="font-medium text-yellow-800">Nessun dispositivo registrato</p>
                      <p className="text-sm text-yellow-600 mt-1">
                        I dispositivi appariranno quando gli utenti installeranno l&apos;app iOS e accetteranno le notifiche push.
                      </p>
                    </div>
                  ) : (
                    <>
                      {/* Tipo di Notifica */}
                      <div className="grid grid-cols-4 gap-2">
                        {[
                          { value: 'promo', label: 'Promo', icon: '🎁', color: 'bg-purple-100 border-purple-300' },
                          { value: 'info', label: 'Info', icon: 'ℹ️', color: 'bg-blue-100 border-blue-300' },
                          { value: 'alert', label: 'Alert', icon: '⚠️', color: 'bg-orange-100 border-orange-300' },
                          { value: 'reminder', label: 'Reminder', icon: '🔔', color: 'bg-green-100 border-green-300' }
                        ].map(type => (
                          <button
                            key={type.value}
                            type="button"
                            onClick={() => setCustomNotification(prev => ({ ...prev, notificationType: type.value }))}
                            className={`p-3 rounded-lg border-2 text-center transition-all ${
                              customNotification.notificationType === type.value 
                                ? `${type.color} border-2` 
                                : 'bg-gray-50 border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <span className="text-2xl block">{type.icon}</span>
                            <span className="text-xs font-medium">{type.label}</span>
                          </button>
                        ))}
                      </div>

                      {/* Titolo e Messaggio */}
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="title" className="font-medium">Titolo *</Label>
                          <Input
                            id="title"
                            value={customNotification.title}
                            onChange={(e) => setCustomNotification(prev => ({ ...prev, title: e.target.value }))}
                            placeholder="Es: Offerta Speciale Weekend!"
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label htmlFor="actionUrl" className="font-medium">Link (opzionale)</Label>
                          <Input
                            id="actionUrl"
                            value={customNotification.actionUrl}
                            onChange={(e) => setCustomNotification(prev => ({ ...prev, actionUrl: e.target.value }))}
                            placeholder="Es: /booking o /events"
                            className="mt-1"
                          />
                        </div>
                      </div>
                      
                      <div>
                        <Label htmlFor="body" className="font-medium">Messaggio *</Label>
                        <Textarea
                          id="body"
                          value={customNotification.body}
                          onChange={(e) => setCustomNotification(prev => ({ ...prev, body: e.target.value }))}
                          placeholder="Scrivi il messaggio della notifica..."
                          rows={3}
                          className="mt-1"
                        />
                        <p className="text-xs text-gray-400 mt-1">{customNotification.body.length}/200 caratteri consigliati</p>
                      </div>

                      {/* Destinatari */}
                      <div className="p-4 bg-gray-50 rounded-lg space-y-3">
                        <Label className="font-medium flex items-center gap-2">
                          <Users className="w-4 h-4" />
                          Destinatari
                        </Label>
                        
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                          {[
                            { value: 'all', label: 'Tutti', count: devices.length, icon: '📢' },
                            { value: 'guests_in_stay', label: 'In Soggiorno', count: guestsInStay.length, icon: '🏠' },
                            { value: 'arriving_soon', label: 'In Arrivo', count: guestsArriving.length, icon: '✈️' },
                            { value: 'selected', label: 'Seleziona', count: customNotification.selectedDevices.length, icon: '✓' }
                          ].map(target => (
                            <button
                              key={target.value}
                              type="button"
                              onClick={() => setCustomNotification(prev => ({ 
                                ...prev, 
                                targetType: target.value,
                                selectedDevices: target.value !== 'selected' ? [] : prev.selectedDevices
                              }))}
                              className={`p-3 rounded-lg border-2 text-center transition-all ${
                                customNotification.targetType === target.value 
                                  ? 'bg-[#1B4332]/10 border-[#1B4332]' 
                                  : 'bg-white border-gray-200 hover:border-gray-300'
                              }`}
                            >
                              <span className="text-xl block">{target.icon}</span>
                              <span className="text-xs font-medium block">{target.label}</span>
                              <span className="text-xs text-gray-500">({target.count})</span>
                            </button>
                          ))}
                        </div>

                        {/* Lista dispositivi per selezione manuale */}
                        {customNotification.targetType === 'selected' && (
                          <div className="border rounded-lg p-3 max-h-48 overflow-y-auto bg-white mt-3">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm font-medium">Seleziona dispositivi:</span>
                              <span className="text-xs text-gray-500">
                                {customNotification.selectedDevices.length} selezionati
                              </span>
                            </div>
                            {devices.length > 0 ? (
                              <div className="space-y-1">
                                {devices.map((device, idx) => (
                                  <label 
                                    key={idx} 
                                    className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${
                                      customNotification.selectedDevices.includes(device.token)
                                        ? 'bg-[#1B4332]/10'
                                        : 'hover:bg-gray-50'
                                    }`}
                                  >
                                    <Checkbox
                                      checked={customNotification.selectedDevices.includes(device.token)}
                                      onCheckedChange={() => toggleDeviceSelection(device.token)}
                                    />
                                    <span className="text-lg">{getDevicePlatformIcon(device.platform)}</span>
                                    <div className="flex-1 min-w-0">
                                      <div className="text-sm font-medium truncate">
                                        {device.user_name || device.user_email || 'Utente Anonimo'}
                                      </div>
                                      <div className="text-xs text-gray-500 truncate">
                                        {device.device_name || device.platform}
                                      </div>
                                    </div>
                                    {(device.user_id || device.user_email) && (
                                      <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                                    )}
                                  </label>
                                ))}
                              </div>
                            ) : (
                              <p className="text-sm text-gray-500 text-center py-4">Nessun dispositivo disponibile</p>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Preview */}
                      {customNotification.title && (
                        <div className="p-4 bg-gray-900 rounded-xl text-white">
                          <p className="text-xs text-gray-400 mb-2">Anteprima notifica</p>
                          <div className="flex items-start gap-3">
                            <div className="w-10 h-10 bg-[#1B4332] rounded-lg flex items-center justify-center font-bold text-sm">
                              M
                            </div>
                            <div className="flex-1">
                              <div className="font-semibold text-sm">{customNotification.title || 'Titolo'}</div>
                              <div className="text-sm text-gray-300 mt-0.5">{customNotification.body || 'Messaggio...'}</div>
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
                <DialogFooter className="gap-2">
                  <Button variant="outline" onClick={() => setCustomDialogOpen(false)}>
                    Annulla
                  </Button>
                  <Button 
                    onClick={sendCustomNotification} 
                    disabled={sending || devices.length === 0 || !customNotification.title || !customNotification.body}
                    className="bg-[#1B4332] text-white"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    {sending ? 'Invio...' : `Invia a ${
                      customNotification.targetType === 'all' ? devices.length :
                      customNotification.targetType === 'guests_in_stay' ? guestsInStay.length :
                      customNotification.targetType === 'arriving_soon' ? guestsArriving.length :
                      customNotification.selectedDevices.length
                    } destinatari`}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            <Button onClick={() => runNotifications('all')} disabled={sending} className="bg-[#1B4332] text-white">
              <Play className={`w-4 h-4 mr-2 ${sending ? 'animate-pulse' : ''}`} />
              Esegui Automatiche
            </Button>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">{error}</div>
        )}

        {lastRunResult && (
          <div className={`border px-4 py-3 rounded-lg ${lastRunResult.errors?.length > 0 ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
            <div className="flex items-center gap-2">
              {lastRunResult.errors?.length > 0 ? (
                <AlertTriangle className="w-5 h-5 text-red-500" />
              ) : (
                <Check className="w-5 h-5 text-green-500" />
              )}
              <span className="font-medium">
                {lastRunResult.total_sent !== undefined 
                  ? `Inviate ${lastRunResult.total_sent} notifiche totali`
                  : `Inviate ${lastRunResult.notifications_sent || 0} notifiche`
                }
              </span>
            </div>
          </div>
        )}

        {/* Quick Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
          <Card className="bg-purple-50 border-purple-200">
            <CardContent className="p-4 text-center">
              <Smartphone className="w-6 h-6 mx-auto text-purple-600 mb-2" />
              <div className="text-2xl font-bold text-purple-700">{devices.length}</div>
              <div className="text-xs text-purple-600">Dispositivi</div>
            </CardContent>
          </Card>
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-4 text-center">
              <Plane className="w-6 h-6 mx-auto text-blue-600 mb-2" />
              <div className="text-2xl font-bold text-blue-700">{guestsArriving.length}</div>
              <div className="text-xs text-blue-600">In Arrivo</div>
            </CardContent>
          </Card>
          <Card className="bg-green-50 border-green-200">
            <CardContent className="p-4 text-center">
              <Users className="w-6 h-6 mx-auto text-green-600 mb-2" />
              <div className="text-2xl font-bold text-green-700">{guestsInStay.length}</div>
              <div className="text-xs text-green-600">In Soggiorno</div>
            </CardContent>
          </Card>
          <Card className="bg-orange-50 border-orange-200">
            <CardContent className="p-4 text-center">
              <LogOut className="w-6 h-6 mx-auto text-orange-600 mb-2" />
              <div className="text-2xl font-bold text-orange-700">{guestsCheckedOut.length}</div>
              <div className="text-xs text-orange-600">Check-out</div>
            </CardContent>
          </Card>
          <Card className="bg-red-50 border-red-200">
            <CardContent className="p-4 text-center">
              <ShoppingCart className="w-6 h-6 mx-auto text-red-600 mb-2" />
              <div className="text-2xl font-bold text-red-700">{abandonedQuotes.length}</div>
              <div className="text-xs text-red-600">Da Recuperare</div>
            </CardContent>
          </Card>
          <Card className="bg-indigo-50 border-indigo-200">
            <CardContent className="p-4 text-center">
              <Bell className="w-6 h-6 mx-auto text-indigo-600 mb-2" />
              <div className="text-2xl font-bold text-indigo-700">{stats?.total_sent || 0}</div>
              <div className="text-xs text-indigo-600">Totali Inviate</div>
            </CardContent>
          </Card>
        </div>

        {/* Weather Card */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              {getWeatherIcon(weatherAnalysis)}
              Meteo Paestum Oggi
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap items-center gap-6">
              {weather && (
                <>
                  <div className="flex items-center gap-2">
                    <Thermometer className="w-5 h-5 text-red-500" />
                    <span className="text-2xl font-bold">{Math.round(weather.temp)}°C</span>
                    <span className="text-gray-500 capitalize">{weather.description}</span>
                  </div>
                  <div className="flex items-center gap-1 text-sm text-gray-500">
                    <Droplets className="w-4 h-4" />
                    <span>{weather.humidity}%</span>
                  </div>
                  <div className="flex items-center gap-1 text-sm text-gray-500">
                    <Wind className="w-4 h-4" />
                    <span>{weather.wind_speed} m/s</span>
                  </div>
                </>
              )}
              {weatherAnalysis && (
                <div className="flex flex-wrap gap-2">
                  {weatherAnalysis.is_beach_day && (
                    <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full">🏖️ Giornata spiaggia</span>
                  )}
                  {weatherAnalysis.is_perfect && (
                    <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">🌟 Perfetta!</span>
                  )}
                  {weatherAnalysis.sunset_special && (
                    <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded-full">🌅 Tramonto speciale</span>
                  )}
                  {weatherAnalysis.alert && (
                    <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full">⚠️ {weatherAnalysis.alert}</span>
                  )}
                </div>
              )}
            </div>
            <div className="flex gap-4 mt-4 overflow-x-auto">
              {forecast.slice(0, 5).map((day, idx) => (
                <div key={idx} className="text-center min-w-[60px] p-2 bg-gray-50 rounded">
                  <div className="text-xs text-gray-500">
                    {new Date(day.date).toLocaleDateString('it-IT', { weekday: 'short' })}
                  </div>
                  <div className="font-bold">{Math.round(day.temp_max)}°</div>
                  <div className="text-xs text-gray-400">{Math.round(day.temp_min)}°</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Scheduler Status Card */}
        {schedulerStatus && (
          <Card className="border-green-200 bg-green-50/50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-3 h-3 rounded-full ${schedulerStatus.running ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`}></div>
                  <div>
                    <span className="font-medium text-green-800">Scheduler Automatico</span>
                    <span className="text-sm text-green-600 ml-2">
                      {schedulerStatus.running ? 'Attivo' : 'Inattivo'}
                    </span>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm text-green-700">
                    <Clock className="w-4 h-4 inline mr-1" />
                    Prossimo invio: <strong>
                      {schedulerStatus.jobs?.[0]?.next_run 
                        ? new Date(schedulerStatus.jobs[0].next_run).toLocaleString('it-IT', {
                            day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'
                          })
                        : 'N/A'}
                    </strong>
                  </div>
                  <div className="text-xs text-green-600 mt-1">
                    Ogni giorno alle 8:00 (Europe/Rome)
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-6">
            <TabsTrigger value="devices" className="flex items-center gap-1">
              <Smartphone className="w-4 h-4" />
              <span className="hidden md:inline">Dispositivi</span>
            </TabsTrigger>
            <TabsTrigger value="during-stay" className="flex items-center gap-1">
              <Users className="w-4 h-4" />
              <span className="hidden md:inline">Durante</span>
            </TabsTrigger>
            <TabsTrigger value="pre-arrival" className="flex items-center gap-1">
              <Plane className="w-4 h-4" />
              <span className="hidden md:inline">Pre-Arrivo</span>
            </TabsTrigger>
            <TabsTrigger value="post-stay" className="flex items-center gap-1">
              <Gift className="w-4 h-4" />
              <span className="hidden md:inline">Post-Stay</span>
            </TabsTrigger>
            <TabsTrigger value="recovery" className="flex items-center gap-1">
              <ShoppingCart className="w-4 h-4" />
              <span className="hidden md:inline">Recupero</span>
            </TabsTrigger>
            <TabsTrigger value="logs" className="flex items-center gap-1">
              <MessageSquare className="w-4 h-4" />
              <span className="hidden md:inline">Log</span>
            </TabsTrigger>
          </TabsList>

          {/* Devices Tab - Professional Device Management Center */}
          <TabsContent value="devices" className="space-y-4">
            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
              <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
                <CardContent className="p-4 text-center">
                  <div className="text-2xl font-bold text-blue-700">{deviceStats?.total || devices.length}</div>
                  <div className="text-xs text-blue-600 font-medium">Totale</div>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
                <CardContent className="p-4 text-center">
                  <div className="text-2xl font-bold text-green-700">{deviceStats?.active || 0}</div>
                  <div className="text-xs text-green-600 font-medium">Attivi</div>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-gray-50 to-gray-100 border-gray-200">
                <CardContent className="p-4 text-center">
                  <div className="text-2xl font-bold text-gray-700">{deviceStats?.ios || 0}</div>
                  <div className="text-xs text-gray-600 font-medium flex items-center justify-center gap-1"><Apple className="w-3 h-3" /> iOS</div>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
                <CardContent className="p-4 text-center">
                  <div className="text-2xl font-bold text-emerald-700">{deviceStats?.android || 0}</div>
                  <div className="text-xs text-emerald-600 font-medium flex items-center justify-center gap-1"><Smartphone className="w-3 h-3" /> Android</div>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
                <CardContent className="p-4 text-center">
                  <div className="text-2xl font-bold text-purple-700">{deviceStats?.admin_devices || 0}</div>
                  <div className="text-xs text-purple-600 font-medium">Admin</div>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
                <CardContent className="p-4 text-center">
                  <div className="text-2xl font-bold text-amber-700">{deviceStats?.guest_devices || 0}</div>
                  <div className="text-xs text-amber-600 font-medium">Ospiti</div>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
                <CardContent className="p-4 text-center">
                  <div className="text-2xl font-bold text-red-700">{deviceStats?.invalid_tokens || 0}</div>
                  <div className="text-xs text-red-600 font-medium">Non validi</div>
                </CardContent>
              </Card>
            </div>

            {/* Health Check */}
            {pushHealth && (
              <Card className="bg-gradient-to-r from-slate-50 to-slate-100">
                <CardContent className="py-3 px-4">
                  <div className="flex flex-wrap items-center gap-4">
                    <span className="text-sm font-medium text-gray-700 flex items-center gap-1">
                      <Activity className="w-4 h-4 text-[#C5A059]" /> Diagnostica:
                    </span>
                    <div className="flex items-center gap-2">
                      {pushHealth.firebase_configured ? (
                        <CheckCircle className="w-4 h-4 text-green-500" />
                      ) : (
                        <XCircle className="w-4 h-4 text-red-500" />
                      )}
                      <span className="text-sm">Firebase</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {pushHealth.backend_online ? (
                        <CheckCircle className="w-4 h-4 text-green-500" />
                      ) : (
                        <XCircle className="w-4 h-4 text-red-500" />
                      )}
                      <span className="text-sm">Backend</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {pushHealth.push_service_online ? (
                        <CheckCircle className="w-4 h-4 text-green-500" />
                      ) : (
                        <XCircle className="w-4 h-4 text-red-500" />
                      )}
                      <span className="text-sm">Push Service</span>
                    </div>
                    {pushHealth.recent_stats && (
                      <div className="text-sm text-gray-500 ml-auto">
                        Success Rate: <span className="font-medium text-green-600">{pushHealth.recent_stats.success_rate}%</span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Filters & Search */}
            <Card>
              <CardContent className="p-4">
                <div className="flex flex-col md:flex-row gap-4">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      placeholder="Cerca per nome, email, dispositivo o token..."
                      value={deviceSearchQuery}
                      onChange={(e) => setDeviceSearchQuery(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Select value={devicePlatformFilter} onValueChange={setDevicePlatformFilter}>
                      <SelectTrigger className="w-[120px]">
                        <SelectValue placeholder="Piattaforma" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Tutte</SelectItem>
                        <SelectItem value="ios">iOS</SelectItem>
                        <SelectItem value="android">Android</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={deviceStatusFilter} onValueChange={setDeviceStatusFilter}>
                      <SelectTrigger className="w-[120px]">
                        <SelectValue placeholder="Stato" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Tutti</SelectItem>
                        <SelectItem value="active">Attivi</SelectItem>
                        <SelectItem value="inactive">Offline</SelectItem>
                        <SelectItem value="invalid">Non validi</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={deviceAdminFilter} onValueChange={setDeviceAdminFilter}>
                      <SelectTrigger className="w-[120px]">
                        <SelectValue placeholder="Tipo" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Tutti</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                        <SelectItem value="guest">Ospiti</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Bulk Actions */}
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" onClick={() => previewRecipients('admin')} disabled={sending} className="gap-2 border-blue-200 text-blue-600">
                <Eye className="w-4 h-4" /> Anteprima Admin
              </Button>
              <Button variant="outline" size="sm" onClick={verifyAdminDevices} disabled={sending} className="gap-2 border-green-200 text-green-600">
                <CheckCircle className="w-4 h-4" /> Verifica Admin
              </Button>
              <Button variant="default" size="sm" onClick={() => bulkTest('admin')} disabled={sending} className="gap-2 bg-[#C5A059] hover:bg-[#B08F4A]">
                <Send className="w-4 h-4" /> Push Admin
              </Button>
              <Button variant="outline" size="sm" onClick={() => bulkTest('ios')} disabled={sending} className="gap-2">
                <Apple className="w-4 h-4" /> Push iOS
              </Button>
              <Button variant="outline" size="sm" onClick={() => bulkTest('android')} disabled={sending} className="gap-2">
                <Smartphone className="w-4 h-4" /> Push Android
              </Button>
              <Button variant="outline" size="sm" onClick={cleanupInactive} className="gap-2 text-red-600 hover:text-red-700 ml-auto">
                <Trash2 className="w-4 h-4" /> Rimuovi inattivi
              </Button>
            </div>

            {/* Devices Table - Desktop */}
            <div className="hidden lg:block">
              <Card>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead className="w-[220px]">Dispositivo</TableHead>
                        <TableHead>Utente</TableHead>
                        <TableHead>Piattaforma</TableHead>
                        <TableHead>Ultima attività</TableHead>
                        <TableHead>Stato</TableHead>
                        <TableHead>Token FCM</TableHead>
                        <TableHead className="text-right">Azioni</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredDevices.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} className="text-center py-12 text-gray-500">
                            <Smartphone className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                            <p>Nessun dispositivo trovato</p>
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredDevices.map((device) => (
                          <React.Fragment key={device.id}>
                            <TableRow 
                              className={`hover:bg-gray-50 cursor-pointer ${expandedDevice === device.id ? 'bg-blue-50' : ''}`}
                              onClick={() => setExpandedDevice(expandedDevice === device.id ? null : device.id)}
                            >
                              <TableCell>
                                <div className="flex items-center gap-3">
                                  <div className="p-2 bg-gray-100 rounded-lg">
                                    {getPlatformIcon(device.platform)}
                                  </div>
                                  <div>
                                    <div className="font-medium text-gray-900 text-sm">
                                      {device.device_name || device.device_model || 'Dispositivo'}
                                    </div>
                                    <div className="text-xs text-gray-500">
                                      {device.os_version || device.platform || 'N/A'}
                                    </div>
                                  </div>
                                </div>
                              </TableCell>
                              <TableCell>
                                <div>
                                  <div className="font-medium text-gray-900 text-sm">
                                    {device.is_admin_device ? (
                                      device.user_nome || device.user_email?.split('@')[0] || 'Admin'
                                    ) : (
                                      device.user_nome || 'Ospite'
                                    )}
                                  </div>
                                  <div className="text-xs text-gray-500">{device.user_email || '—'}</div>
                                </div>
                              </TableCell>
                              <TableCell>
                                <Badge variant="outline" className="gap-1 text-xs">
                                  {getPlatformIcon(device.platform)}
                                  {(device.platform || 'Unknown').toUpperCase()}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                <div className="text-xs text-gray-600">
                                  {formatDate(device.last_registered)}
                                </div>
                              </TableCell>
                              <TableCell>{getStatusBadge(device)}</TableCell>
                              <TableCell>
                                <div className="flex items-center gap-1">
                                  <code className="text-xs bg-gray-100 px-2 py-1 rounded font-mono">
                                    {device.token_preview || device.token?.substring(0, 12) + '...'}
                                  </code>
                                  <Button variant="ghost" size="icon" className="h-6 w-6" onClick={(e) => { e.stopPropagation(); copyToken(device.token); }}>
                                    <Copy className="w-3 h-3" />
                                  </Button>
                                  <Button variant="ghost" size="icon" className="h-6 w-6" onClick={(e) => { e.stopPropagation(); setSelectedDevice(device); setShowTokenDialog(true); }}>
                                    <Eye className="w-3 h-3" />
                                  </Button>
                                </div>
                              </TableCell>
                              <TableCell className="text-right">
                                <div className="flex items-center justify-end gap-1">
                                  <Button variant="ghost" size="icon" className="h-7 w-7 text-blue-600" onClick={(e) => { e.stopPropagation(); testSingleDevice(device.id); }} disabled={sending}>
                                    <Bell className="w-4 h-4" />
                                  </Button>
                                  <Button variant="ghost" size="icon" className="h-7 w-7 text-gray-600" onClick={(e) => { e.stopPropagation(); setSelectedDevice(device); fetchDeviceHistory(device.id); }}>
                                    <History className="w-4 h-4" />
                                  </Button>
                                  <Button variant="ghost" size="icon" className="h-7 w-7 text-red-600" onClick={(e) => { e.stopPropagation(); setSelectedDevice(device); setShowDeleteDialog(true); }}>
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                  {expandedDevice === device.id ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                                </div>
                              </TableCell>
                            </TableRow>
                            {expandedDevice === device.id && (
                              <TableRow>
                                <TableCell colSpan={7} className="bg-slate-50 p-4">
                                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                    <div><span className="text-gray-500">Device ID:</span><div className="font-mono text-xs truncate">{device.id}</div></div>
                                    <div><span className="text-gray-500">User ID:</span><div className="font-mono text-xs truncate">{device.user_id || 'N/A'}</div></div>
                                    <div><span className="text-gray-500">App Version:</span><div>{device.app_version || 'N/A'}</div></div>
                                    <div><span className="text-gray-500">Admin Device:</span><div>{device.is_admin_device ? '✅ Sì' : '❌ No'}</div></div>
                                  </div>
                                </TableCell>
                              </TableRow>
                            )}
                          </React.Fragment>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </div>

            {/* Devices Cards - Mobile/Tablet */}
            <div className="lg:hidden space-y-3">
              {filteredDevices.length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center text-gray-500">
                    <Smartphone className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>Nessun dispositivo trovato</p>
                  </CardContent>
                </Card>
              ) : (
                filteredDevices.map((device) => (
                  <Card key={device.id} className="overflow-hidden">
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <div className="p-3 bg-gray-100 rounded-xl">
                          {getPlatformIcon(device.platform)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-semibold text-gray-900 truncate text-sm">
                              {device.device_name || device.device_model || 'Dispositivo'}
                            </h3>
                            {getStatusBadge(device)}
                          </div>
                          <div className="text-xs text-gray-500">
                            {(device.platform || 'Unknown').toUpperCase()} {device.os_version && `• ${device.os_version}`}
                          </div>
                          <div className="text-sm text-gray-700 mt-1">
                            {device.is_admin_device ? (device.user_nome || device.user_email?.split('@')[0] || 'Admin') : (device.user_nome || device.user_email || 'Ospite')}
                          </div>
                          <div className="text-xs text-gray-400 mt-1">Ultimo accesso: {formatDate(device.last_registered)}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 mt-4 pt-3 border-t">
                        <code className="text-xs bg-gray-100 px-2 py-1 rounded font-mono flex-1 truncate">
                          {device.token_preview || device.token?.substring(0, 20) + '...'}
                        </code>
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => copyToken(device.token)}><Copy className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setSelectedDevice(device); setShowTokenDialog(true); }}><Eye className="w-4 h-4" /></Button>
                      </div>
                      <div className="flex items-center gap-2 mt-3">
                        <Button variant="outline" size="sm" className="flex-1 gap-1" onClick={() => testSingleDevice(device.id)} disabled={sending}>
                          <Bell className="w-4 h-4" /> Test
                        </Button>
                        <Button variant="outline" size="sm" className="flex-1 gap-1" onClick={() => { setSelectedDevice(device); fetchDeviceHistory(device.id); }}>
                          <History className="w-4 h-4" /> Storico
                        </Button>
                        <Button variant="outline" size="sm" className="gap-1 text-red-600" onClick={() => { setSelectedDevice(device); setShowDeleteDialog(true); }}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>

            {/* Push Logs Card */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Clock className="w-5 h-5 text-[#C5A059]" />
                  Ultime Push Inviate
                </CardTitle>
              </CardHeader>
              <CardContent>
                {pushLogs.length > 0 ? (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {pushLogs.slice(0, 10).map((log, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg text-sm">
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-gray-900 truncate">{log.title}</div>
                          <div className="text-xs text-gray-500 truncate">{log.body}</div>
                        </div>
                        <div className="text-right ml-3 shrink-0">
                          <div className="flex items-center gap-1">
                            <span className="text-green-600 font-medium text-xs">{log.successful || log.sent || 0}✓</span>
                            <span className="text-red-600 font-medium text-xs">{log.failed || 0}✗</span>
                          </div>
                          <div className="text-xs text-gray-400">{formatDate(log.created_at || log.sent_at)}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-4 text-sm">Nessuna push inviata</p>
                )}
              </CardContent>
            </Card>

            {/* Token Dialog */}
            <Dialog open={showTokenDialog} onOpenChange={setShowTokenDialog}>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Shield className="w-5 h-5 text-[#C5A059]" />
                    Token FCM Completo
                  </DialogTitle>
                  <DialogDescription>Token del dispositivo {selectedDevice?.device_name}</DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="p-4 bg-gray-100 rounded-lg">
                    <code className="text-xs font-mono break-all select-all">{selectedDevice?.token}</code>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div><span className="text-gray-500">Lunghezza:</span><div className="font-medium">{selectedDevice?.token?.length || 0} caratteri</div></div>
                    <div><span className="text-gray-500">Ultima modifica:</span><div className="font-medium">{formatDate(selectedDevice?.last_registered)}</div></div>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowTokenDialog(false)}>Chiudi</Button>
                  <Button onClick={() => { copyToken(selectedDevice?.token); setShowTokenDialog(false); }}><Copy className="w-4 h-4 mr-2" /> Copia Token</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Delete Dialog */}
            <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2 text-red-600"><Trash2 className="w-5 h-5" /> Rimuovi Dispositivo</DialogTitle>
                  <DialogDescription>Sei sicuro di voler rimuovere questo dispositivo? Non riceverà più notifiche push.</DialogDescription>
                </DialogHeader>
                <div className="p-4 bg-red-50 rounded-lg">
                  <div className="font-medium">{selectedDevice?.device_name || 'Dispositivo'}</div>
                  <div className="text-sm text-gray-600">{selectedDevice?.user_nome || selectedDevice?.user_email || 'Utente'}</div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>Annulla</Button>
                  <Button variant="destructive" onClick={() => deleteDevice(selectedDevice?.id)}><Trash2 className="w-4 h-4 mr-2" /> Rimuovi</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* History Dialog */}
            <Dialog open={showHistoryDialog} onOpenChange={setShowHistoryDialog}>
              <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2"><History className="w-5 h-5 text-[#C5A059]" /> Storico Notifiche - {selectedDevice?.device_name}</DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                  {deviceHistory.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <Bell className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p>Nessuna notifica trovata</p>
                    </div>
                  ) : (
                    deviceHistory.map((log, idx) => (
                      <div key={idx} className="p-3 border rounded-lg bg-white">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="font-medium text-gray-900">{log.title}</div>
                            <div className="text-sm text-gray-600 mt-1">{log.body}</div>
                          </div>
                          <div className="text-right ml-4">
                            <div className="flex items-center gap-1">
                              {log.successful > 0 ? <CheckCircle className="w-4 h-4 text-green-500" /> : <XCircle className="w-4 h-4 text-red-500" />}
                              <span className={`text-sm ${log.successful > 0 ? 'text-green-600' : 'text-red-600'}`}>{log.successful > 0 ? 'Inviata' : 'Fallita'}</span>
                            </div>
                            <div className="text-xs text-gray-400 mt-1">{formatDate(log.created_at)}</div>
                          </div>
                        </div>
                        {log.result?.errors && log.result.errors.length > 0 && (
                          <div className="mt-2 text-xs text-red-600 bg-red-50 p-2 rounded">Errore: {log.result.errors[0]?.error_code || 'Unknown'}</div>
                        )}
                      </div>
                    ))
                  )}
                </div>
                <DialogFooter><Button variant="outline" onClick={() => setShowHistoryDialog(false)}>Chiudi</Button></DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Preview Recipients Dialog */}
            <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
              <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2"><Eye className="w-5 h-5 text-blue-500" /> Anteprima Destinatari Push</DialogTitle>
                  <DialogDescription>Dispositivi che riceveranno la notifica (target: {previewData?.target})</DialogDescription>
                </DialogHeader>
                {previewData && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-3 gap-3">
                      <div className="p-3 bg-blue-50 rounded-lg text-center">
                        <div className="text-2xl font-bold text-blue-700">{previewData.summary?.total_devices || 0}</div>
                        <div className="text-xs text-blue-600">Totale</div>
                      </div>
                      <div className="p-3 bg-green-50 rounded-lg text-center">
                        <div className="text-2xl font-bold text-green-700">{previewData.summary?.included || 0}</div>
                        <div className="text-xs text-green-600">Inclusi</div>
                      </div>
                      <div className="p-3 bg-red-50 rounded-lg text-center">
                        <div className="text-2xl font-bold text-red-700">{previewData.summary?.excluded || 0}</div>
                        <div className="text-xs text-red-600">Esclusi</div>
                      </div>
                    </div>
                    
                    {previewData.included_devices?.length > 0 && (
                      <div>
                        <h4 className="font-medium text-green-700 mb-2 flex items-center gap-1"><CheckCircle className="w-4 h-4" /> Inclusi ({previewData.included_devices.length})</h4>
                        <div className="space-y-2 max-h-40 overflow-y-auto">
                          {previewData.included_devices.map((d, i) => (
                            <div key={i} className="flex items-center justify-between p-2 bg-green-50 rounded text-sm">
                              <div>
                                <span className="font-medium">{d.device_name}</span>
                                <span className="text-gray-500 ml-2">{d.user_nome}</span>
                              </div>
                              <Badge variant="outline" className="text-green-600">{d.platform?.toUpperCase()}</Badge>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {previewData.excluded_devices?.length > 0 && (
                      <div>
                        <h4 className="font-medium text-red-700 mb-2 flex items-center gap-1"><XCircle className="w-4 h-4" /> Esclusi ({previewData.excluded_devices.length})</h4>
                        <div className="space-y-2 max-h-40 overflow-y-auto">
                          {previewData.excluded_devices.map((d, i) => (
                            <div key={i} className="flex items-center justify-between p-2 bg-red-50 rounded text-sm">
                              <div>
                                <span className="font-medium">{d.device_name}</span>
                                <span className="text-gray-500 ml-2">{d.user_nome}</span>
                              </div>
                              <span className="text-xs text-red-600">{d.exclusion_reasons?.join(', ')}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                <DialogFooter><Button variant="outline" onClick={() => setShowPreviewDialog(false)}>Chiudi</Button></DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Verify Admin Devices Dialog */}
            <Dialog open={showVerifyDialog} onOpenChange={setShowVerifyDialog}>
              <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    {verifyData?.overall_status === 'ready' ? (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    ) : (
                      <AlertTriangle className="w-5 h-5 text-yellow-500" />
                    )}
                    Verifica Dispositivi Admin
                  </DialogTitle>
                </DialogHeader>
                {verifyData && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-3 gap-3">
                      <div className="p-3 bg-blue-50 rounded-lg text-center">
                        <div className="text-2xl font-bold text-blue-700">{verifyData.total_admin_devices}</div>
                        <div className="text-xs text-blue-600">Dispositivi Admin</div>
                      </div>
                      <div className="p-3 bg-green-50 rounded-lg text-center">
                        <div className="text-2xl font-bold text-green-700">{verifyData.ready}</div>
                        <div className="text-xs text-green-600">Pronti</div>
                      </div>
                      <div className="p-3 bg-red-50 rounded-lg text-center">
                        <div className="text-2xl font-bold text-red-700">{verifyData.with_issues}</div>
                        <div className="text-xs text-red-600">Con problemi</div>
                      </div>
                    </div>
                    
                    {verifyData.issues_summary?.length > 0 && (
                      <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <h4 className="font-medium text-yellow-700 mb-2">Problemi rilevati:</h4>
                        <ul className="text-sm text-yellow-600 space-y-1">
                          {verifyData.issues_summary.map((issue, i) => (
                            <li key={i}>• {issue}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    <div className="space-y-2">
                      {verifyData.devices?.map((d, i) => (
                        <div key={i} className={`p-3 rounded-lg border ${d.status === 'ready' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              {d.status === 'ready' ? (
                                <CheckCircle className="w-4 h-4 text-green-500" />
                              ) : (
                                <XCircle className="w-4 h-4 text-red-500" />
                              )}
                              <span className="font-medium">{d.device_name}</span>
                              <Badge variant="outline">{d.platform?.toUpperCase()}</Badge>
                            </div>
                            <span className="text-sm text-gray-500">{d.user_nome}</span>
                          </div>
                          <div className="grid grid-cols-3 gap-2 text-xs">
                            <div className={d.checks?.token_presente ? 'text-green-600' : 'text-red-600'}>
                              {d.checks?.token_presente ? '✓' : '✗'} Token presente
                            </div>
                            <div className={d.checks?.token_valido ? 'text-green-600' : 'text-red-600'}>
                              {d.checks?.token_valido ? '✓' : '✗'} Token valido
                            </div>
                            <div className={d.checks?.token_length_ok ? 'text-green-600' : 'text-yellow-600'}>
                              {d.checks?.token_length_ok ? '✓' : '⚠'} Lunghezza ({d.token_length} chars)
                            </div>
                            <div className={d.checks?.user_id_presente ? 'text-green-600' : 'text-red-600'}>
                              {d.checks?.user_id_presente ? '✓' : '✗'} User ID
                            </div>
                            <div className={d.checks?.ruolo_admin ? 'text-green-600' : 'text-red-600'}>
                              {d.checks?.ruolo_admin ? '✓' : '✗'} Ruolo admin
                            </div>
                            <div className={d.checks?.dispositivo_attivo ? 'text-green-600' : 'text-red-600'}>
                              {d.checks?.dispositivo_attivo ? '✓' : '✗'} Attivo
                            </div>
                          </div>
                          {d.issues?.length > 0 && (
                            <div className="mt-2 text-xs text-red-600">
                              Problemi: {d.issues.join(', ')}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <DialogFooter><Button variant="outline" onClick={() => setShowVerifyDialog(false)}>Chiudi</Button></DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Test Result Dialog */}
            <Dialog open={showTestResultDialog} onOpenChange={setShowTestResultDialog}>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    {testResult?.success ? (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-500" />
                    )}
                    Risultato Test Push
                  </DialogTitle>
                </DialogHeader>
                {testResult && (
                  <div className="space-y-4">
                    <div className={`p-4 rounded-lg ${testResult.success ? 'bg-green-50' : 'bg-red-50'}`}>
                      <div className="font-medium text-lg">{testResult.status_message || (testResult.success ? 'Notifica inviata' : 'Invio fallito')}</div>
                    </div>
                    
                    {testResult.device && (
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <h4 className="font-medium mb-2">Dispositivo</h4>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <div><span className="text-gray-500">Nome:</span> {testResult.device.name}</div>
                          <div><span className="text-gray-500">Piattaforma:</span> {testResult.device.platform}</div>
                          <div className="col-span-2"><span className="text-gray-500">Token:</span> <code className="text-xs">{testResult.device.token_preview}</code></div>
                        </div>
                      </div>
                    )}
                    
                    {testResult.firebase && (
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <h4 className="font-medium mb-2">Risposta Firebase</h4>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <div><span className="text-gray-500">Inviate:</span> <span className="text-green-600 font-medium">{testResult.firebase.sent}</span></div>
                          <div><span className="text-gray-500">Fallite:</span> <span className="text-red-600 font-medium">{testResult.firebase.failed}</span></div>
                          <div><span className="text-gray-500">Tempo risposta:</span> {testResult.firebase.response_time_ms}ms</div>
                          {testResult.firebase.message_id && (
                            <div><span className="text-gray-500">Message ID:</span> <code className="text-xs">{testResult.firebase.message_id}</code></div>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {testResult.summary && (
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <h4 className="font-medium mb-2">Riepilogo</h4>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <div><span className="text-gray-500">Dispositivi trovati:</span> {testResult.summary.total_devices}</div>
                          <div><span className="text-gray-500">Token validi:</span> {testResult.summary.valid_tokens}</div>
                          <div><span className="text-gray-500">Accettate Firebase:</span> <span className="text-green-600">{testResult.summary.accepted_by_firebase}</span></div>
                          <div><span className="text-gray-500">Rifiutate Firebase:</span> <span className="text-red-600">{testResult.summary.rejected_by_firebase}</span></div>
                        </div>
                      </div>
                    )}
                    
                    {testResult.error && (
                      <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                        <h4 className="font-medium text-red-700 mb-1">Errore</h4>
                        <div className="text-sm text-red-600">
                          <div><strong>{testResult.error.code}</strong></div>
                          <div>{testResult.error.description}</div>
                        </div>
                      </div>
                    )}
                    
                    {testResult.errors && testResult.errors.length > 0 && (
                      <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                        <h4 className="font-medium text-red-700 mb-1">Errori per dispositivo</h4>
                        {testResult.errors.map((err, i) => (
                          <div key={i} className="text-sm text-red-600">• {err.device_name}: {err.description}</div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                <DialogFooter><Button variant="outline" onClick={() => setShowTestResultDialog(false)}>Chiudi</Button></DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Bulk Confirm Dialog */}
            <Dialog open={showBulkConfirmDialog} onOpenChange={setShowBulkConfirmDialog}>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2"><Send className="w-5 h-5 text-[#C5A059]" /> Conferma Invio Push</DialogTitle>
                  <DialogDescription>Verifica i destinatari prima di inviare</DialogDescription>
                </DialogHeader>
                {bulkPreview && (
                  <div className="space-y-4">
                    <div className="p-4 bg-blue-50 rounded-lg text-center">
                      <div className="text-3xl font-bold text-blue-700">{bulkPreview.summary?.will_send_to || 0}</div>
                      <div className="text-blue-600">dispositivi riceveranno la notifica</div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div className="p-2 bg-gray-50 rounded">
                        <span className="text-gray-500">Target:</span> <span className="font-medium">{bulkPreview.target?.toUpperCase()}</span>
                      </div>
                      <div className="p-2 bg-gray-50 rounded">
                        <span className="text-gray-500">Esclusi:</span> <span className="font-medium text-red-600">{bulkPreview.summary?.excluded || 0}</span>
                      </div>
                    </div>
                    
                    {bulkPreview.recipients?.length > 0 && (
                      <div>
                        <h4 className="font-medium mb-2">Destinatari:</h4>
                        <div className="max-h-32 overflow-y-auto space-y-1">
                          {bulkPreview.recipients.map((r, i) => (
                            <div key={i} className="flex items-center justify-between text-sm p-1 bg-gray-50 rounded">
                              <span>{r.device_name}</span>
                              <span className="text-gray-500">{r.user_nome}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                <DialogFooter className="gap-2">
                  <Button variant="outline" onClick={() => setShowBulkConfirmDialog(false)}>Annulla</Button>
                  <Button onClick={() => bulkTest(bulkPreview?.target, true)} disabled={sending} className="bg-[#C5A059] hover:bg-[#B08F4A]">
                    <Send className="w-4 h-4 mr-2" /> Invia a {bulkPreview?.summary?.will_send_to || 0} dispositivi
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* During Stay Tab */}
          <TabsContent value="during-stay">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    Ospiti in Soggiorno ({guestsInStay.length})
                  </CardTitle>
                  <Button size="sm" onClick={() => runNotifications('')} disabled={sending}>
                    <Send className="w-4 h-4 mr-2" />
                    Invia Suggerimenti
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {guestsInStay.length > 0 ? (
                  <div className="space-y-3">
                    {guestsInStay.map((guest, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <div className="font-medium">{guest.name}</div>
                          <div className="text-sm text-gray-500">{guest.unit_name}</div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="text-right">
                            <div className="text-sm font-medium text-green-600">Giorno {guest.day_of_stay}</div>
                            <div className="text-xs text-gray-400">{guest.check_in} → {guest.check_out}</div>
                          </div>
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="border-[#1B4332] text-[#1B4332]"
                            onClick={() => sendEntryCode(guest.booking_id)}
                            disabled={sending}
                          >
                            <Key className="w-4 h-4 mr-1" />
                            Invia Codice
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-8">Nessun ospite attualmente in soggiorno</p>
                )}
                <div className="mt-4 p-4 bg-green-50 rounded-lg">
                  <h4 className="font-medium mb-2 text-green-800">Notifiche Durante il Soggiorno:</h4>
                  <ul className="text-sm text-green-700 space-y-1">
                    <li>🏖️ Suggerimenti spiagge basati sul meteo</li>
                    <li>🍽️ Ristoranti consigliati</li>
                    <li>✨ Esperienze da non perdere</li>
                    <li>🎉 Eventi del giorno</li>
                    <li>⚠️ Alert meteo quando necessario</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Pre-Arrival Tab */}
          <TabsContent value="pre-arrival">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Plane className="w-5 h-5" />
                    In Arrivo nei Prossimi 3 Giorni ({guestsArriving.length})
                  </CardTitle>
                  <Button size="sm" onClick={() => runNotifications('pre-arrival')} disabled={sending}>
                    <Send className="w-4 h-4 mr-2" />
                    Invia Pre-Arrivo
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {guestsArriving.length > 0 ? (
                  <div className="space-y-3">
                    {guestsArriving.map((guest, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                        <div>
                          <div className="font-medium">{guest.name}</div>
                          <div className="text-sm text-gray-500">{guest.unit_name || guest.email}</div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="text-right">
                            <div className="text-sm font-medium text-blue-600">
                              {guest.days_until_arrival === 1 ? 'Domani' : 
                               guest.days_until_arrival === 0 ? 'Oggi' : 
                               `Tra ${guest.days_until_arrival} giorni`}
                            </div>
                            <div className="text-xs text-gray-400">Check-in: {guest.check_in}</div>
                            {!guest.online_checkin_completed && (
                              <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded">Check-in da fare</span>
                            )}
                          </div>
                          {(guest.days_until_arrival <= 1) && (
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="border-[#1B4332] text-[#1B4332]"
                              onClick={() => sendEntryCode(guest.booking_id)}
                              disabled={sending}
                            >
                              <Key className="w-4 h-4 mr-1" />
                              Invia Codice
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-8">Nessun arrivo previsto nei prossimi 3 giorni</p>
                )}
                <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                  <h4 className="font-medium mb-2 text-blue-800">Notifiche Pre-Arrivo Automatiche:</h4>
                  <ul className="text-sm text-blue-700 space-y-1">
                    <li>🌤️ <strong>3 giorni prima:</strong> Meteo + cosa portare</li>
                    <li>📝 <strong>2 giorni prima:</strong> Check-in online + Info ZTL</li>
                    <li>🏠 <strong>1 giorno prima:</strong> Benvenuto + consigli primo giorno</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Post-Stay Tab */}
          <TabsContent value="post-stay">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Gift className="w-5 h-5" />
                    Check-out Ultimi 7 Giorni ({guestsCheckedOut.length})
                  </CardTitle>
                  <Button size="sm" onClick={() => runNotifications('post-stay')} disabled={sending}>
                    <Send className="w-4 h-4 mr-2" />
                    Invia Post-Stay
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {guestsCheckedOut.length > 0 ? (
                  <div className="space-y-3">
                    {guestsCheckedOut.map((guest, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-orange-50 rounded-lg">
                        <div>
                          <div className="font-medium">{guest.name}</div>
                          <div className="text-sm text-gray-500">{guest.email}</div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm font-medium text-orange-600">
                            {guest.days_since_checkout === 1 ? 'Ieri' : `${guest.days_since_checkout} giorni fa`}
                          </div>
                          {guest.review_submitted ? (
                            <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded">Recensione ✓</span>
                          ) : (
                            <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">No recensione</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-8">Nessun check-out negli ultimi 7 giorni</p>
                )}
                <div className="mt-4 p-4 bg-orange-50 rounded-lg">
                  <h4 className="font-medium mb-2 text-orange-800">Notifiche Post-Soggiorno:</h4>
                  <ul className="text-sm text-orange-700 space-y-1">
                    <li>💚 <strong>Giorno 1:</strong> Grazie + richiesta recensione</li>
                    <li>⭐ <strong>Giorno 3:</strong> Reminder recensione</li>
                    <li>🎁 <strong>Giorno 7:</strong> Codice sconto 10%</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Recovery Tab */}
          <TabsContent value="recovery">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <ShoppingCart className="w-5 h-5" />
                    Preventivi Abbandonati ({abandonedQuotes.length})
                  </CardTitle>
                  <Button size="sm" onClick={() => runNotifications('recovery')} disabled={sending}>
                    <Send className="w-4 h-4 mr-2" />
                    Invia Recupero
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {abandonedQuotes.length > 0 ? (
                  <div className="space-y-3">
                    {abandonedQuotes.map((quote, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                        <div>
                          <div className="font-medium">{quote.name}</div>
                          <div className="text-sm text-gray-500">{quote.unit_name}</div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm font-medium text-red-600">€{quote.total_price}</div>
                          <div className="text-xs text-gray-400">{quote.check_in} → {quote.check_out}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-8">Nessun preventivo abbandonato</p>
                )}
              </CardContent>
            </Card>

            {/* Discount Codes */}
            <Card className="mt-4">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Gift className="w-5 h-5" />
                  Codici Sconto Generati
                </CardTitle>
              </CardHeader>
              <CardContent>
                {discountCodes.length > 0 ? (
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {discountCodes.map((code, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm">
                        <code className="font-mono font-bold text-[#1B4332]">{code.code}</code>
                        <div className="flex items-center gap-4">
                          <span>{code.discount_percent}% off</span>
                          <span className={code.used ? 'text-gray-400' : 'text-green-600'}>
                            {code.used ? 'Usato' : 'Attivo'}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-4">Nessun codice sconto generato</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Logs Tab */}
          <TabsContent value="logs">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageSquare className="w-5 h-5" />
                  Storico Notifiche Smart
                </CardTitle>
              </CardHeader>
              <CardContent>
                {logs.length > 0 ? (
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {logs.map((log, idx) => (
                      <div key={idx} className="flex items-center justify-between border-b pb-3 last:border-0">
                        <div className="flex items-center gap-3">
                          <span className="text-xl">{getNotificationTypeIcon(log.type)}</span>
                          <div>
                            <p className="font-medium">{log.content?.title}</p>
                            <p className="text-sm text-gray-500 truncate max-w-md">{log.content?.body}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-xs text-gray-500">
                            {new Date(log.sent_at).toLocaleDateString('it-IT', { 
                              day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' 
                            })}
                          </div>
                          <div className="flex gap-2 mt-1">
                            {log.opened && <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded">Aperta</span>}
                            {log.clicked && <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded">Cliccata</span>}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-8">Nessuna notifica smart inviata</p>
                )}
              </CardContent>
            </Card>

            {/* Stats */}
            {stats && (
              <Card className="mt-4">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="w-5 h-5" />
                    Statistiche
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center p-3 bg-gray-50 rounded">
                      <div className="text-2xl font-bold">{stats.total_sent}</div>
                      <div className="text-xs text-gray-500">Totale Inviate</div>
                    </div>
                    <div className="text-center p-3 bg-gray-50 rounded">
                      <div className="text-2xl font-bold">{stats.last_7_days}</div>
                      <div className="text-xs text-gray-500">Ultimi 7 Giorni</div>
                    </div>
                    <div className="text-center p-3 bg-gray-50 rounded">
                      <div className="text-2xl font-bold">{stats.open_rate}%</div>
                      <div className="text-xs text-gray-500">Tasso Apertura</div>
                    </div>
                    <div className="text-center p-3 bg-gray-50 rounded">
                      <div className="text-2xl font-bold">{stats.click_rate}%</div>
                      <div className="text-xs text-gray-500">Tasso Clic</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
};

export default SmartNotificationsPage;
